import { cacheAllModuleFiles, updateModuleCache } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import dataUtils from '@/common/dataUtils';
import videosApi from './videosApi';
import moduleIndex from './index.js';
import favoritesService from '@/services/favoritesService';
import index from './index.js';
import utils from '@/common/utils';

const MODULE_ID = index.moduleId;
const USER_STATE_TYPE = 'videoUserState';

const state = {
  videos: [],
  videosById: {},
  groups: [],
  videoUserStates: [],

  thumbs: [],

  // --- UI states
  dataLoaded: false,
  selectedGroup: null,
  unreadCountByModuleInstance: {},
};
const getters = {
  sortedAndFilteredGroups(state) {
    return state.groups
      .filter((g) => g._used === true)
      .slice()
      .sort((g) => g.name);
  },
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.unreadCountByModuleInstance[instance];
  },
};
const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    let unreadCountByModuleInstance = {};
    state.videos.forEach((v) => {
      let count = unreadCountByModuleInstance[v.instance] || 0;
      if (!v._userState.dateread) count++;
      unreadCountByModuleInstance[v.instance] = count;
    });
    state.unreadCountByModuleInstance = unreadCountByModuleInstance;
  },

  setVideosData(state, data) {
    Object.keys(data).forEach((key) => (state[key] = data[key]));
    console.debug('setVideosData');
    state.dataLoaded = true;
  },
  setSelectedGroup(state, selectedGroup) {
    state.selectedGroup = selectedGroup;
  },
};
const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId: MODULE_ID,
      items: state.videos,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  // TODO: DELETE as soon as nowhere used
  async updateModuleApplicationCache({ state }) {
    // const { groups, videos, thumbs, videoUserStates } = state;
    // await updateModuleCache(moduleIndex.api, { groups, videos, thumbs, videoUserStates });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    let urls = state.videos.map((v) => utils.constructFileUrl(moduleIndex.api, v, 'filename'));
    // await cacheAllModuleFiles({ files: state.videos.concat(state.thumbs), moduleIndex, commit, dispatch });
    // Hint to cache Audio files: append audio files to the files attribute
    await cacheAllModuleFiles({ files: state.thumbs, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, rootState, dispatch, rootGetters }) {
    try {
      let response = await videosApi.getVideos();
      let { groups, videos, thumbs, videoUserStates } = response.data;

      let videosById = dataUtils.buildIdMap(videos, 'id');
      let groupsById = dataUtils.buildIdMap(groups, 'id');
      let userStatesByVideoId = dataUtils.buildIdMap(videoUserStates, 'videoIdFk');

      thumbs.forEach((t) => {
        let video = videosById[t.videoIdFk];
        if (video) {
          video._poster = t;
        }
      });
      videos.forEach((v) => {
        if (v.groupIdFk) {
          let group = groupsById[v.groupIdFk];
          if (group) {
            group._used = true;
            v._group = group;
          }
        }
        // Create a userState object in case it does not exist
        let userState;
        if (userStatesByVideoId[v.id]) {
          userState = userStatesByVideoId[v.id];
        }
        if (!userState) {
          // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
          userState = { videoIdFk: v.id, favorite: 0, dateread: null, dateconfirmed: null };
        }
        favoritesService.assertFavoriteIsSet(v);

        v._userState = userState;
        v._userState._type = USER_STATE_TYPE;

        //enhance item with information used when searching
        let moduleConfig = rootGetters['configs/getModuleConfig']({
          moduleType: moduleIndex.moduleType,
          instance: v.instance,
        });

        // add calculated data used mainly for displaying in live-search
        v._displayLocation = '';
        //enhance item with information used for example when searching
        v._moduleTitle = moduleConfig ? moduleConfig.title : 'module not defined';
        v._path = `/videos/${v.instance}/${v.id}`;
        v._moduleId = MODULE_ID;
        // Object.freeze(v);
      });
      favoritesService.addFavoriteStatesFromLocalStorage({ moduleId: MODULE_ID, items: videos });

      // apply user states stored in localStorage if the userState is newer than what is stored on the item.
      // Delete from local storage if we have a newer state on the object
      // Do not delete otherwise as it is obvious now, the state has not been stored on backend.
      dataUtils.applyLocalStorageUserState(videosById, USER_STATE_TYPE, 'videoIdFk');

      commit('setVideosData', { videos, videosById, groups, thumbs, videoUserStates });
    } catch (err) {
      commit(
        'sync/setSyncState',
        {
          moduleId: MODULE_ID,
          syncState: SYNC_STATES.FAILURE,
          msg: `${err.code}:${err.message}`,
        },
        { root: true }
      );
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
